import React, { useEffect, useRef, useState } from 'react';
import { TextTemplate, TextTemplateCategory } from '../../types';
import { createTextTemplate, deleteTextTemplate } from '../../services/crmService';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { ChevronDown, Trash2, FilePlus, Loader2, FileText } from 'lucide-react';

interface Props {
  category: TextTemplateCategory;
  // Bereits nach Kategorie gefilterte Bausteine (Gruppierung im Editor).
  templates: TextTemplate[];
  // Aktueller Feldinhalt (fuer Ersetzen-Rueckfrage + "Speichern"-Deaktivierung).
  currentText: string;
  onInsert: (content: string) => void;
  onCreated: (t: TextTemplate) => void;
  onDeleted: (id: string) => void;
}

/**
 * Kompaktes "Vorlagen"-Dropdown neben einem Angebots-Textfeld. Listet die
 * Bausteine der jeweiligen Kategorie, fuegt einen ausgewaehlten Baustein ins
 * Feld ein (Ersetzen nur nach Rueckfrage, wenn das Feld nicht leer ist),
 * loescht Bausteine und speichert den aktuellen Feldtext als neuen Baustein.
 * Der Service-Aufruf liegt hier; der Editor haelt nur die lokale Liste aktuell.
 */
export const TextTemplatePicker: React.FC<Props> = ({
  category,
  templates,
  currentText,
  onInsert,
  onCreated,
  onDeleted,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Klick ausserhalb schliesst das Dropdown (Muster aus dem Katalog-Dropdown).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const handleInsert = (content: string) => {
    // Leeres Feld -> direkt einfuegen; sonst erst bestaetigen lassen.
    if (currentText.trim() && !confirm('Vorhandenen Text ersetzen?')) return;
    onInsert(content);
    setOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteTextTemplate(id);
      onDeleted(id);
    } catch (e) {
      alert('Vorlage konnte nicht gelöscht werden: ' + rechtFehlerOder(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveCurrent = async () => {
    const name = prompt('Name der Vorlage:');
    if (!name || !name.trim()) return;
    setBusy(true);
    try {
      const created = await createTextTemplate({ category, name: name.trim(), content: currentText });
      onCreated(created);
    } catch (e) {
      alert('Vorlage konnte nicht gespeichert werden: ' + rechtFehlerOder(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors"
        title="Textbausteine"
      >
        <FileText className="w-3.5 h-3.5" />
        Vorlagen
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in">
          <div className="max-h-56 overflow-y-auto py-1">
            {templates.length === 0 ? (
              <div className="px-4 py-5 text-center text-xs text-slate-400">Noch keine Vorlagen.</div>
            ) : (
              templates.map((t) => (
                <div key={t.id} className="flex items-center gap-2 px-2 hover:bg-blue-50/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => handleInsert(t.content)}
                    className="flex-1 min-w-0 py-2 px-2 text-left text-sm text-slate-700 truncate"
                    title={t.content}
                  >
                    {t.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id)}
                    disabled={busy}
                    className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all shrink-0 disabled:opacity-40"
                    title="Vorlage löschen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-slate-100 p-1.5">
            <button
              type="button"
              onClick={handleSaveCurrent}
              disabled={busy || !currentText.trim()}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title={currentText.trim() ? 'Aktuellen Feldtext als Vorlage speichern' : 'Feld ist leer'}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FilePlus className="w-3.5 h-3.5" />}
              Aktuellen Text als Vorlage speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
